import { useEffect, useState } from "react";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { Input, Label } from "./ui/Input";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export function AccountSecurityPanel() {
  const token = getSessionToken();
  const [hasPassword, setHasPassword] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) setHasPassword(Boolean(json.data.hasPassword));
      })
      .finally(() => setLoaded(true));
  }, [token]);

  async function savePassword(endpoint: string, body: Record<string, string>) {
    if (!token) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to save password");
      setMessage(json.message || "Password saved.");
      setHasPassword(true);
      setPassword("");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="space-y-4">
      {!loaded ? (
        <p className="text-sm text-content-muted">Loading account settings…</p>
      ) : (
        <>
          <div>
            <h2 className="text-lg font-semibold text-content-primary">Password</h2>
            <p className="mt-1 text-sm text-content-secondary">
              Set a password to sign in with email and password.
            </p>
          </div>

          {hasPassword ? (
            <div className="space-y-3">
              <Label>
                Current password
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1.5"
                />
              </Label>
              <Label>
                New password
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1.5"
                />
              </Label>
              <Button
                type="button"
                disabled={saving}
                onClick={() => savePassword("change-password", { currentPassword, newPassword })}
              >
                {saving ? "Saving…" : "Change password"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Label>
                New password (min 8 characters)
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                />
              </Label>
              <Button
                type="button"
                disabled={saving}
                onClick={() => savePassword("set-password", { password })}
              >
                {saving ? "Saving…" : "Set password"}
              </Button>
            </div>
          )}

          {message && <p className="text-sm text-status-active-text">{message}</p>}
          {error && <p className="text-sm text-status-rejected-text">{error}</p>}
        </>
      )}
    </Card>
  );
}
